// src/components/events/EventLandingPage.tsx
//
// RCCG LEAP-style event landing page.
// • Pure Tailwind CSS — no inline style objects except where Tailwind
//   cannot express dynamic values (e.g. arbitrary hex colours from JSON).
// • No custom fontFamily declarations — uses the project's default
//   Tailwind font stack.
// • Main brand colour: green (green-800 / green-700 / green-600).
// • CTAs navigate to /event/register via react-router <Link>.
// • onRegisterClick prop still accepted for modal-based flows.

import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Wifi,
  Users,
  ChevronRight,
  ChevronDown,
  Globe,
} from "lucide-react";
import type { Speaker } from "../../types/eventTypes";
import { staticEvent, type StaticEventData } from "../../data/staticEvent";
export { staticEvent };

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────── */

const PLACEHOLDER = "/src/assets/images/adeolu.png";

/* ─────────────────────────────────────────────────────────────────
   COUNTDOWN HOOK
───────────────────────────────────────────────────────────────── */

interface TL {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

function useCountdown(target: string): TL {
  const calc = (): TL => {
    const d = new Date(target).getTime() - Date.now();
    if (d <= 0)
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
    return {
      days: Math.floor(d / 86_400_000),
      hours: Math.floor((d % 86_400_000) / 3_600_000),
      minutes: Math.floor((d % 3_600_000) / 60_000),
      seconds: Math.floor((d % 60_000) / 1_000),
      isPast: false,
    };
  };
  const [t, set] = useState<TL>(calc);
  useEffect(() => {
    const id = setInterval(() => set(calc()), 1000);
    return () => clearInterval(id);
  }, [target]); // eslint-disable-line
  return t;
}

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const pad = (n: number) => String(n).padStart(2, "0");
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
const fmtTime = (d: string) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

/* ─────────────────────────────────────────────────────────────────
   COUNTDOWN BOX
───────────────────────────────────────────────────────────────── */

const CountBox: React.FC<{ value: number; label: string }> = ({
  value,
  label,
}) => (
  <div className="flex flex-col items-center gap-2">
    <div className="flex items-center justify-center rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 text-white font-bold text-2xl sm:text-3xl md:text-4xl tabular-nums">
      {pad(value)}
    </div>
    <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/40">
      {label}
    </span>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   SECTION TITLE
───────────────────────────────────────────────────────────────── */

const SectionTitle: React.FC<{ text: string; light?: boolean }> = ({
  text,
  light,
}) => (
  <div className="text-center mb-12">
    <h2
      className={`text-2xl sm:text-3xl md:text-4xl font-black tracking-wide uppercase mb-4 ${light ? "text-white" : "text-gray-900"}`}
    >
      {text}
    </h2>
    <div className="w-14 h-1 bg-green-600 mx-auto rounded-full" />
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   NAV LINK
───────────────────────────────────────────────────────────────── */

const NL: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    className="text-white/70 hover:text-white text-xs font-semibold tracking-widest uppercase transition-colors duration-200 no-underline"
  >
    {children}
  </a>
);

/* ─────────────────────────────────────────────────────────────────
   SPEAKER CARD  (circular portrait)
───────────────────────────────────────────────────────────────── */

const SpeakerCard: React.FC<{ speaker: Speaker }> = ({ speaker }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="flex flex-col items-center text-center max-w-[180px]">
      <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full overflow-hidden border-4 border-green-600 shadow-[0_0_0_4px_rgba(255,255,255,0.06),0_8px_32px_rgba(0,0,0,0.45)] mb-4 shrink-0">
        <img
          src={err || !speaker.photoUrl ? PLACEHOLDER : speaker.photoUrl}
          alt={speaker.name}
          onError={() => setErr(true)}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p className="font-bold text-sm sm:text-base text-white mb-1 leading-snug">
        {speaker.name}
      </p>
      {speaker.title && (
        <p className="text-[11px] text-white/50 italic mb-1.5 leading-snug">
          ({speaker.title})
        </p>
      )}
      {speaker.topic && (
        <p className="text-[10px] font-black tracking-widest uppercase text-green-400">
          {speaker.topic}
        </p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   GALLERY GRID  (masonry + lightbox)
───────────────────────────────────────────────────────────────── */

// const GalleryGrid: React.FC<{ images: string[] }> = ({ images }) => {
//   const [sel, setSel] = useState<string | null>(null);
//   return (
//     <>
//       <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-2">
//         {images.map((src, i) => (
//           <div
//             key={i}
//             onClick={() => setSel(src)}
//             className="break-inside-avoid mb-2 overflow-hidden rounded-md cursor-pointer hover:opacity-80 transition-opacity duration-200"
//           >
//             <img src={src} alt="" className="w-full block object-cover" />
//           </div>
//         ))}
//       </div>
//       {sel && (
//         <div
//           onClick={() => setSel(null)}
//           className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-6"
//         >
//           <img
//             src={sel}
//             alt=""
//             className="max-w-[88vw] max-h-[88vh] object-contain rounded-lg"
//             onClick={(e) => e.stopPropagation()}
//           />
//         </div>
//       )}
//     </>
//   );
// };

/* ─────────────────────────────────────────────────────────────────
   PROPS
───────────────────────────────────────────────────────────────── */

export type { StaticEventData };

export interface EventLandingPageProps {
  event?: StaticEventData;
  /** Called when a Register button is clicked (use for modal flows).
   *  If omitted, buttons navigate to /event/register via <Link>. */
  onRegisterClick?: () => void;
  isRegistrationOpen?: boolean;
  registerLabel?: string;
  galleryImages?: string[];
  className?: string;
}

/* ─────────────────────────────────────────────────────────────────
   REGISTER CTA — Link or button depending on prop
───────────────────────────────────────────────────────────────── */

const RegisterCTA: React.FC<{
  label: string;
  onRegisterClick?: () => void;
  size?: "sm" | "md" | "lg";
}> = ({ label, onRegisterClick, size = "md" }) => {
  const base =
    "inline-flex items-center gap-2 font-black uppercase tracking-widest bg-green-600 hover:bg-green-500 active:scale-95 transition-all text-white rounded shadow-lg shadow-green-900/40";
  const sizes = {
    sm: "text-xs px-5 py-2.5",
    md: "text-sm px-8 py-3.5",
    lg: "text-sm px-10 py-4",
  };
  const cls = `${base} ${sizes[size]}`;

  if (onRegisterClick) {
    return (
      <button onClick={onRegisterClick} className={cls}>
        {label} <ChevronRight size={14} />
      </button>
    );
  }
  return (
    <Link to="/event/register" className={cls}>
      {label} <ChevronRight size={14} />
    </Link>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const EventLandingPage: React.FC<EventLandingPageProps> = ({
  event: eventProp,
  onRegisterClick,
  isRegistrationOpen,
  registerLabel = "Register Now",
  galleryImages: galleryProp,
  className = "",
}) => {
  const event = eventProp ?? staticEvent;
  const open = isRegistrationOpen ?? event.registrationOpen;
  const cd = useCountdown(event.eventDate);

  const speakers = useMemo(
    () => [...event.speakers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [event.speakers],
  );
  const agenda = useMemo(
    () => [...event.agenda].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [event.agenda],
  );
  //   const gallery = useMemo(
  //     () =>
  //       galleryProp?.length
  //         ? galleryProp
  //         : (event as StaticEventData).galleryImages?.length
  //           ? (event as StaticEventData).galleryImages!
  //           : Array(8).fill(PLACEHOLDER),
  //     [galleryProp, event],
  //   );

  return (
    <div className={`min-h-screen bg-white ${className}`}>
      {/* ── STICKY NAV ── */}
      <header className="sticky top-0 z-50 bg-green-950/95 backdrop-blur-md border-b border-white/5 h-16 flex items-center justify-between px-4 md:px-10 gap-5">
        <div className="flex items-center gap-3">
          {event.logoUrl && (
            <img src={event.logoUrl} alt="" className="h-8 object-contain" />
          )}
          <span className="text-white font-black text-sm tracking-widest uppercase truncate max-w-[200px]">
            {event.shortName}
          </span>
        </div>

        <nav className="hidden md:flex items-center gap-7">
          <NL href="#about">About</NL>
          {speakers.length > 0 && <NL href="#speakers">Speakers</NL>}
          {agenda.length > 0 && <NL href="#agenda">Agenda</NL>}
          <NL href="#gallery">Gallery</NL>
          <NL href="#contact">Contact</NL>
        </nav>

        {open && (
          <RegisterCTA
            label={registerLabel}
            onRegisterClick={onRegisterClick}
            size="sm"
          />
        )}
      </header>

      {/* ── HERO ── */}
      <section
        id="home"
        className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 md:px-10 py-20 overflow-hidden"
      >
        {/* Background image */}
        <img
          src={event.bannerImageUrl || PLACEHOLDER}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark green overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-green-950/80 via-green-950/85 to-green-950/95" />

        {/* Decorative rings */}
        <div className="absolute -right-[10%] -top-[10%] w-[50vw] h-[50vw] rounded-full border border-white/5 pointer-events-none" />
        <div className="absolute -right-[5%] -top-[5%] w-[35vw] h-[35vw] rounded-full border border-white/[0.04] pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl w-full flex flex-col items-center gap-6">
          {/* Tagline pill */}
          {event.tagline && (
            <div className="inline-flex items-center gap-2 border border-green-500/60 rounded-full px-5 py-2 text-green-400 text-[11px] font-black tracking-[0.22em] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {event.tagline}
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight drop-shadow-2xl">
            {event.name}
          </h1>

          {/* Meta strip */}
          <p className="text-[11px] sm:text-xs text-white/60 font-semibold tracking-widest uppercase leading-loose">
            {event.tagline && (
              <>
                <strong className="text-white/90">Theme:</strong>{" "}
                {event.tagline}
                <span className="mx-3 opacity-35">·</span>
              </>
            )}
            <strong className="text-white/90">Date:</strong>{" "}
            {fmtDate(event.eventDate)}
            <span className="mx-3 opacity-35">·</span>
            <strong className="text-white/90">Time:</strong>{" "}
            {fmtTime(event.eventDate)}
            {event.timezone ? ` (${event.timezone})` : ""}
            {event.venue && (
              <>
                <span className="mx-3 opacity-35">·</span>
                {event.venue}
              </>
            )}
          </p>

          {/* Countdown */}
          {!cd.isPast && (
            <div className="flex flex-col items-center gap-4 mt-2">
              <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/35">
                Event starts in
              </p>
              <div className="flex items-end gap-2 sm:gap-4">
                <CountBox value={cd.days} label="Days" />
                <span className="text-2xl text-white/20 font-light mb-6">
                  :
                </span>
                <CountBox value={cd.hours} label="Hours" />
                <span className="text-2xl text-white/20 font-light mb-6">
                  :
                </span>
                <CountBox value={cd.minutes} label="Minutes" />
                <span className="text-2xl text-white/20 font-light mb-6">
                  :
                </span>
                <CountBox value={cd.seconds} label="Seconds" />
              </div>
            </div>
          )}

          {/* CTA */}
          {open && !cd.isPast ? (
            <RegisterCTA
              label={registerLabel}
              onRegisterClick={onRegisterClick}
              size="lg"
            />
          ) : !open ? (
            <div className="border border-white/20 text-white/45 text-sm px-7 py-3 rounded">
              Registration is closed
            </div>
          ) : (
            <div className="border border-white/20 text-white/45 text-sm px-7 py-3 rounded">
              Event has passed
            </div>
          )}

          {/* Scroll cue */}
          <ChevronDown
            className="text-white/25 mt-8 animate-bounce"
            size={22}
          />
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="bg-white py-20 md:py-24 px-4 md:px-10">
        <div className="max-w-4xl mx-auto">
          <SectionTitle text="Welcome" />

          {event.description && (
            <p className="max-w-2xl mx-auto text-center text-base md:text-lg text-gray-600 leading-relaxed mb-12">
              {event.description}
            </p>
          )}

          {/* Detail strip */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 py-7 border-t border-b border-gray-100 mb-10">
            {[
              {
                icon: (
                  <Calendar size={16} className="text-green-600 shrink-0" />
                ),
                label: fmtDate(event.eventDate),
              },
              {
                icon: <Clock size={16} className="text-green-600 shrink-0" />,
                label: `${fmtTime(event.eventDate)}${event.eventEndDate ? ` – ${fmtTime(event.eventEndDate)}` : ""}`,
              },
              event.isVirtual
                ? {
                    icon: (
                      <Wifi size={16} className="text-green-600 shrink-0" />
                    ),
                    label: "Live Online",
                  }
                : event.venue
                  ? {
                      icon: (
                        <MapPin size={16} className="text-green-600 shrink-0" />
                      ),
                      label: event.venue,
                    }
                  : null,
              (event.registrationCount ?? 0) > 0
                ? {
                    icon: (
                      <Users size={16} className="text-green-600 shrink-0" />
                    ),
                    label: `${event.registrationCount!.toLocaleString()} registered`,
                  }
                : null,
            ]
              .filter(Boolean)
              .map(
                (item, i) =>
                  item && (
                    <div key={i} className="flex items-center gap-2.5">
                      {item.icon}
                      <span className="text-sm text-gray-700 font-semibold">
                        {item.label}
                      </span>
                    </div>
                  ),
              )}
          </div>

          {open && (
            <div className="text-center">
              <RegisterCTA
                label={registerLabel}
                onRegisterClick={onRegisterClick}
              />
            </div>
          )}
        </div>
      </section>

      {/* ── GALLERY ── */}
      {/* <section
        id="gallery"
        className="bg-green-950 py-20 md:py-24 px-4 md:px-10"
      >
        <div className="max-w-6xl mx-auto">
          <SectionTitle text="From Our Gallery" light />
          <GalleryGrid images={gallery} />
        </div>
      </section> */}

      {/* ── SPEAKERS ── */}
      {speakers.length > 0 && (
        <section
          id="speakers"
          className="bg-green-900 py-20 md:py-24 px-4 md:px-10"
        >
          <div className="max-w-6xl mx-auto">
            <SectionTitle text="Speakers" light />
            <div className="flex flex-wrap justify-center gap-10 md:gap-16 row-gap-14">
              {speakers.map((s, i) => (
                <SpeakerCard key={s._id ?? i} speaker={s} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── AGENDA ── */}

      {/* ── CONCEPT ── */}
      <section className="bg-gradient-to-br from-green-950 to-green-900 py-20 md:py-24 px-4 md:px-10">
        <div className="max-w-2xl mx-auto text-center">
          <SectionTitle text={event.tagline || "About"} light />

          {event.description && (
            <p className="text-base md:text-lg text-white/65 leading-relaxed mb-10">
              {event.description}
            </p>
          )}

          {agenda.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2.5 mb-12">
              {agenda.slice(0, 6).map((a, i) => (
                <span
                  key={i}
                  className="px-4 py-1.5 rounded-full border border-green-500/40 text-green-400 text-xs font-bold tracking-widest uppercase"
                >
                  {a.title}
                </span>
              ))}
            </div>
          )}

          {open && (
            <RegisterCTA
              label={registerLabel}
              onRegisterClick={onRegisterClick}
              size="lg"
            />
          )}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="bg-white py-20 md:py-24 px-4 md:px-10">
        <div className="max-w-xl mx-auto text-center">
          <SectionTitle text="Contact" />
          <p className="text-gray-500 text-base leading-relaxed mb-7">
            For enquiries feel free to send us a message or email us.
          </p>
          {event.contactNo && (
            <a
              href={event.contactNo}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-green-600 font-bold text-sm hover:text-green-700 transition-colors no-underline"
            >
              <Globe size={15} /> {event.contactNo}
            </a>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-green-950 px-4 md:px-10 py-7 flex flex-wrap items-center justify-between gap-4">
        <p className="text-white/35 text-sm">
          {event.name} © {new Date().getFullYear()}. All rights reserved.
        </p>
        <div className="flex gap-6">
          {[
            { label: "Privacy Policy", href: event.privacyPolicyUrl },
            { label: "Data Protection", href: event.dataProtectionUrl },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="text-white/35 text-sm hover:text-white transition-colors no-underline"
            >
              {l.label}
            </a>
          ))}
        </div>
      </footer>

      {/* Keyframes for pulse/bounce — Tailwind animate-bounce covers the scroll cue */}
      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.35} }
      `}</style>
    </div>
  );
};

export default EventLandingPage;
